import { orderBy } from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import { Collapse, Table, Typography, Button, Modal, Input, type TableProps } from "antd";
import { EyeOutlined, SearchOutlined } from "@ant-design/icons";
import { useGetWorkerDocuments } from "@src/appV2/WorkerDocuments/api/useGetWorkerDocuments";
import { Resources } from "@src/appV2/WorkerDocuments/types/resources";
import { type WorkerDocument } from "@src/appV2/WorkerDocuments/types/workerDocument.types";
import { type Requirement } from "@src/appV2/Requirement/types";
import { fetchDocumentDistributionUrl } from "@src/appV2/WorkerDocuments/api/fetchDocumentDistributionUrl";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { DOCUMENT_STATUS_METADATA } from "../../../constants/documents";
import { ShowError } from "../../../components/error";
import { formatCreatedAt, renderExpiryCell, renderStatusCell } from "./documentFieldUtils";
import { logError } from "@src/appV2/lib/analytics";

const { Text } = Typography;

const SORT_ORDER = { ASCENDING: "asc", DESCENDING: "desc" } as const;
type SORT_ORDER_TYPE = (typeof SORT_ORDER)[keyof typeof SORT_ORDER];

interface DeletedDocumentTableProps {
  agentId: string;
  agentTMZ?: string;
}

interface DeletedDocumentRow {
  id: string;
  requirementName: string;
  deletionReason: string;
  createdAt?: string;
  status: string;
  expiry?: string;
  requirement?: Requirement | null;
}

function getRequirementName(requirement?: Requirement | null): string {
  return requirement?.attributes?.name ?? "Miscellaneous";
}

function orderDocuments(
  documents: DeletedDocumentRow[],
  field: string,
  order: "asc" | "desc"
): DeletedDocumentRow[] {
  const sortField =
    field === "status"
      ? (row: DeletedDocumentRow) => DOCUMENT_STATUS_METADATA[row.status]?.order ?? 0
      : field;

  return orderBy(documents, [sortField], [order]);
}

function mapDocumentsToRows(documents: WorkerDocument[]): DeletedDocumentRow[] {
  return documents.map((document) => ({
    id: document.id,
    requirementName: getRequirementName(document.attributes.requirement),
    deletionReason: "",
    createdAt: document.attributes.createdAt,
    status: document.attributes.status,
    expiry: document.attributes.expiry,
    requirement: document.attributes.requirement,
  }));
}

function renderRequirementName(requirementName: string) {
  return <div style={{ wordWrap: "break-word", wordBreak: "break-word" }}>{requirementName}</div>;
}

export function ArchivedDocumentTable({ agentId, agentTMZ }: DeletedDocumentTableProps) {
  const [searchText, setSearchText] = useState("");
  const [previewLoadingId, setPreviewLoadingId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewTitle, setPreviewTitle] = useState<string>("");
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<SORT_ORDER_TYPE>(SORT_ORDER.DESCENDING);

  const { data, isLoading, error, refetch } = useGetWorkerDocuments(
    {
      filter: {
        hcpId: agentId,
        archived: true,
        fetchLatest: false,
      },
      include: [Resources.REQUIREMENTS],
    },
    { enabled: !!agentId }
  );

  const handleCollapseChange = (activeKeys: string | string[]) => {
    const keys = Array.isArray(activeKeys) ? activeKeys : [activeKeys];
    if (keys.includes("deleted-documents")) {
      void refetch();
    }
  };

  const tableRows = useMemo(() => {
    const rows = mapDocumentsToRows(data ?? []);
    return orderDocuments(rows, sortField, sortOrder);
  }, [data, sortField, sortOrder]);

  const handleTableChange: TableProps<DeletedDocumentRow>["onChange"] = (
    _pagination,
    _filters,
    sorter
  ) => {
    if (!sorter || Array.isArray(sorter) || !sorter.field || !sorter.order) {
      return;
    }
    setSortField(sorter.field as string);
    setSortOrder(sorter.order === "ascend" ? SORT_ORDER.ASCENDING : SORT_ORDER.DESCENDING);
  };

  const handlePreview = useCallback(
    async (documentId: string) => {
      try {
        setPreviewLoadingId(documentId);
        const doc = tableRows.find((row) => row.id === documentId);
        const distributionUrl = await fetchDocumentDistributionUrl({ documentId });
        setPreviewUrl(distributionUrl);
        setPreviewTitle(doc?.requirementName ?? "Document preview");
        setPreviewVisible(true);
      } catch (previewError: unknown) {
        showErrorToast("Unable to open document");
        logError("archived_document_preview_error", { error: previewError });
      } finally {
        setPreviewLoadingId(null);
      }
    },
    [tableRows]
  );

  const columns = useMemo<TableProps<DeletedDocumentRow>["columns"]>(
    () => [
      {
        title: "Name",
        dataIndex: "requirementName",
        sorter: true,
        render: renderRequirementName,
      },
      {
        title: "Created",
        dataIndex: "createdAt",
        render: (createdAt?: string) => {
          const formatted = formatCreatedAt(createdAt, agentTMZ);
          return formatted ? <Text>{formatted}</Text> : null;
        },
        sorter: true,
        width: 150,
      },
      {
        title: "Deletion Reason",
        dataIndex: "deletionReason",
        render: () => "",
      },
      {
        title: "Status",
        dataIndex: "status",
        render: renderStatusCell,
        sorter: true,
      },
      {
        title: "Expiry",
        render: (_expiry, row) =>
          renderExpiryCell({
            expiryType: row.requirement?.attributes?.expiryType,
            expiryDate: row.expiry,
            wrapWithPopover: false,
          }),
        sorter: true,
        dataIndex: "expiry",
      },
      {
        title: "",
        dataIndex: "id",
        render: (_, row) => (
          <Button
            size="small"
            onClick={() => handlePreview(row.id)}
            loading={previewLoadingId === row.id}
            aria-label="Preview document"
          >
            <EyeOutlined />
          </Button>
        ),
        width: 60,
      },
    ],
    [agentTMZ, handlePreview, previewLoadingId]
  );

  const filteredTableRows = searchText
    ? tableRows.filter((row) =>
        row.requirementName.toLowerCase().includes(searchText.toLowerCase())
      )
    : tableRows;

  return (
    <>
      <Collapse onChange={handleCollapseChange}>
        <Collapse.Panel header="Deleted Documents" key="deleted-documents">
          <Input
            allowClear
            placeholder="Search by document name..."
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
            }}
            style={{ marginBottom: 16, maxWidth: 350 }}
          />
          <Table
            columns={columns}
            dataSource={filteredTableRows}
            loading={isLoading}
            pagination={{ hideOnSinglePage: true, showSizeChanger: false }}
            rowKey="id"
            size="small"
            onChange={handleTableChange}
            locale={{
              emptyText: error ? (
                <ShowError message="Unable to load deleted documents" error={error} />
              ) : undefined,
            }}
          />
        </Collapse.Panel>
      </Collapse>

      <Modal
        visible={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={() => {
          setPreviewVisible(false);
          setPreviewUrl(null);
        }}
        width="60vw"
        bodyStyle={{ height: "70vh" }}
        destroyOnClose
      >
        {previewUrl ? (
          <iframe
            title="document-preview"
            src={previewUrl}
            style={{ border: "none", width: "100%", height: "100%", padding: 10 }}
          />
        ) : (
          <div style={{ padding: 16 }}>
            <Text>Unable to load preview.</Text>
          </div>
        )}
      </Modal>
    </>
  );
}
